import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Valet from './Valet';
import { GARAGES } from '../constants/garages';
import { getRouteInfo } from '../config/routes';
import { createScopedLogger } from '../utils/logger.js';
// ClientPortalContext removed — active portal uses PortalContext via PortalShell

const log = createScopedLogger('ParkingLot');

/**
 * ParkingLot - Dynamic component loader based on garage/lot routing
 * 
 * This component determines which bricksOS component to load
 * based on the current garage and lot from the URL.
 * 
 * Just like a real parking lot where each space has a specific vehicle,
 * each lot in our garage has a specific business component (brick).
 */
const ParkingLot = () => {
  const { garageId, lotId } = useParams();
  const location = useLocation();

  // Check routes.js for correct lot/vehicle mapping
  const routeInfo = getRouteInfo(location.pathname);
  
  // Parse pathname manually if routeInfo doesn't have garageId/lotId
  // Handle paths like /admin/client-portal
  let actualGarageId = routeInfo?.garageId || garageId;
  let actualLotId = routeInfo?.lotId || lotId;
  
  if (!actualGarageId && location.pathname) {
    // Try to parse from pathname: /admin/client-portal -> garageId: admin, lotId: client-portal
    const pathParts = location.pathname.split('/').filter(p => p);
    if (pathParts.length >= 2) {
      actualGarageId = pathParts[0];
      actualLotId = pathParts[1];
    } else if (pathParts.length === 1) {
      actualGarageId = pathParts[0];
    }
  }
  
  const vehicleName = routeInfo?.vehicleName; // Get vehicle from route config
  const brickId = routeInfo?.brickId; // Get brickId from route config

  // Find the current garage and lot configuration
  const garage = GARAGES.find(g => g.id === actualGarageId);
  const lot = garage?.lots?.find(l => l.id === actualLotId);


  
  // If no garage found
  if (!garage) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl text-gray-400 mb-2">🚫 Garage Not Found</h2>
          <p className="text-gray-500">
            The requested garage "{actualGarageId}" could not be found.
          </p>
        </div>
      </div>
    );
  }
  
  // Garage overview — the actionable landing for "no lot" AND "lot not found".
  // A bare not-found screen is a dead-end; this always offers somewhere to go.
  const renderGarageOverview = (notice) => (
    <div className="h-full w-full p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-4">
          {garage.name} Garage
        </h1>
        <p className="text-gray-400 mb-8">
          {notice || garage.description || 'Select a lot from the sidebar to get started.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {garage.lots?.map(l => (
            <div
              key={l.id}
              className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-yellow-600 transition-colors cursor-pointer"
              onClick={() => window.location.href = `/${actualGarageId}/${l.id}`}
            >
              <h3 className="text-xl font-semibold text-white mb-2">{l.name}</h3>
              <p className="text-gray-400 text-sm">{l.description || 'Manage ' + l.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // If garage but no lot - show the garage overview
  if (!actualLotId) {
    log.info?.('Showing garage overview', { actualGarageId });
    return renderGarageOverview();
  }
  
  // If lot not found in garage AND no vehicleName from routes.js
  // routes.js can map URLs to vehicles that aren't in the GARAGES sidebar config
  if (!lot && !vehicleName) {
    log.info?.('Lot not found — falling back to garage overview', { actualGarageId, actualLotId });
    return renderGarageOverview(
      `"${actualLotId}" is no longer part of the ${garage.name} garage. Pick a lot below.`
    );
  }
  
  // Get the lot path for loading components from the lots directory
  // The lot path matches the actualLotId to find components in the correct directory
  // Example: actualLotId='contracts' → looks in '/lots/contracts/'
  const lotPath = actualLotId;

  // Get component name from lot configuration
  // Priority order:
  // 1. routes.js vehicleName (handles URL mismatch with Lots structure)
  // 2. lot.parking.component (for fullscreen layouts)
  // 3. lot.parking.slots.main (for standard layouts)
  // 4. lot.component (current standard)
  // 5. lot.componentName (legacy support)
  // 6. 'index' (default fallback)
  let componentName = vehicleName || 'index';

  // Only check lot config if vehicleName wasn't specified in routes.js
  if (!vehicleName) {
    if (lot.parking) {
      if (lot.parking.component) {
        // Fullscreen layout with single component
        componentName = lot.parking.component;
      } else if (lot.parking.slots?.main) {
        // Standard layout with main slot
        componentName = lot.parking.slots.main;
      }
    } else if (lot.component) {
      // Current standard: use component property
      componentName = lot.component;
    } else if (lot.componentName) {
      // Legacy support for direct componentName
      componentName = lot.componentName;
    }
  }

  // Log a warning only if we're truly falling back to index due to missing configuration
  if (componentName === 'index' && !vehicleName && !lot.component && !lot.componentName && !lot.parking?.component && !lot.parking?.slots?.main) {
    log.warn?.('No component specified, using fallback', { garageId, actualLotId });
  }

  log.debug?.('Loading valet component', {
    garageId,
    urlLotId: lotId,
    actualLotId,
    lotPath,
    componentName,
    fromRoutesJs: !!vehicleName
  });

  log.debug?.('Valet props being passed', {
    lotId: lotPath,
    vehicleName: componentName,
    expectedVehicleKey: `${lotPath}.${componentName}`
  });

  const content = (
    <Valet
      lotId={lotPath}
      vehicleName={componentName}
      brickId={brickId}
      garage={garage}
      lotConfig={lot}
      autoUnload={false}
    />
  );

  return <div className="h-full w-full">{content}</div>;
};

export default ParkingLot;
